import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { SupportedLanguage } from '../QuestionPlayer';
import { de } from './locales/de';
import { en } from './locales/en';
import { pl } from './locales/pl';
import { ua } from './locales/ua';
import { AppMessages } from './types';

const LANGUAGE_STORAGE_KEY = 'pj.language';

const messages: Record<SupportedLanguage, AppMessages> = {
  pl,
  en,
  de,
  ua,
};

type I18nContextValue = {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
  t: AppMessages;
};

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

function readInitialLanguage(): SupportedLanguage {
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY) as SupportedLanguage | null;
  return stored && messages[stored] ? stored : 'pl';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(readInitialLanguage);

  const setLanguage = useCallback((nextLanguage: SupportedLanguage) => {
    setLanguageState(nextLanguage);
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: messages[language],
    }),
    [language],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}
